// Turns a step's answers into the short human phrase its chip shows.
//
// The rule: a chip says what the customer told us, in their words, not what we
// called the question. "Europe" beats "Destination", "12–20 Mar" beats "Dates".
// That is the whole reason the ribbon works as navigation — you recognise your
// own answer far faster than you recognise a step name.

import { labelFor } from "../../reference/labelCache";
import { keyFor } from "../../reference/referenceKey";

/** @typedef {import("../../form/types").StepConfig} StepConfig */
/** @typedef {import("../../form/types").FieldSchema} FieldSchema */

/**
 * Which fields a step's chip summarises.
 *
 * A step may name them explicitly with `summary: ["coverage", "destination"]`.
 * Otherwise we take the first two required fields, which is the right guess
 * often enough that most steps never need the key.
 *
 * @param {StepConfig} step
 * @returns {string[]}
 */
export const summaryFieldsOf = (step) => {
  if (Array.isArray(step.summary) && step.summary.length) return step.summary;
  const fields = (step.form?.sections || []).flatMap((s) => s.fields || []);
  const required = fields.filter((f) => f.required || f.requiredWhen);
  return (required.length ? required : fields).slice(0, 2).map((f) => f.name);
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const formatCurrency = (value) => {
  const n = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isNaN(n) ? String(value) : `AED ${n.toLocaleString("en-AE")}`;
};

/**
 * One field's value as display text.
 *
 * Reference-backed fields hold canonical codes ("AE", "GULF"), which must never
 * reach a customer's screen. If the list is already loaded we show its label;
 * if not, we fall back to the raw value rather than blocking the render.
 *
 * @param {FieldSchema} field
 * @param {*} value
 * @param {Record<string, *>} values
 */
export const formatAnswer = (field, value, values) => {
  if (value === undefined || value === null || value === "") return "";

  if (field.type === "checkbox" || field.type === "switch" || field.type === "consent") {
    return value ? field.label : "";
  }
  if (field.type === "date") return formatDate(value);
  if (field.type === "currency") return formatCurrency(value);
  if (field.type === "phone") return `+${String(value)}`;

  const source = field.options;
  if (Array.isArray(source)) {
    return source.find((o) => o.value === value)?.label ?? String(value);
  }
  if (source?.source === "reference") {
    // `decorate` may rename an option for its card ("For myself" → "Individual").
    // The chip has to show whatever the customer saw when they picked it, or
    // the ribbon quietly disagrees with the screen it is summarising.
    const renamed = source.decorate?.[value]?.label;
    if (renamed) return renamed;

    // The cached list is keyed by the same params the fetch used, so rebuild
    // them here — including every dependency when there is more than one.
    const parents = [source.dependsOn ?? []].flat().filter(Boolean);
    const params = parents.length
      ? Object.fromEntries(parents.map((field) => [field, values?.[field]]))
      : source.params;
    return labelFor(keyFor(source.domain, params), value) || String(value);
  }
  return String(value);
};

/**
 * The chip text for a step, or "" when it has nothing worth showing (a
 * document-upload or plan-selection step, or one that was skipped).
 *
 * @param {StepConfig} step
 * @param {Record<string, *>} values
 * @returns {string}
 */
export const summariseStep = (step, values) => {
  if (!step.form) return "";
  const names = summaryFieldsOf(step);
  const fields = (step.form.sections || []).flatMap((s) => s.fields || []);

  const parts = names
    .map((name) => {
      const field = fields.find((f) => f.name === name);
      return field ? formatAnswer(field, values?.[name], values) : "";
    })
    .filter(Boolean);

  if (!parts.length) return "";

  // Two dates read as a range rather than two separate facts.
  const bothDates = names
    .map((n) => fields.find((f) => f.name === n))
    .every((f) => f?.type === "date");
  const joined = parts.length === 2 && bothDates ? parts.join(" – ") : parts.join(" · ");

  return joined.length > 32 ? `${joined.slice(0, 31)}…` : joined;
};
